// Dynamic, colorful, photoreal Solar System. createElement to bypass visual-edits.
import { useRef, useMemo, useEffect, useState, createElement as h } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

const EARTH_DAY = "https://threejs.org/examples/textures/planets/earth_atmos_2048.jpg";
const EARTH_CLOUDS = "https://threejs.org/examples/textures/planets/earth_clouds_1024.png";

function useTextureSet(urls) {
  const [tex, setTex] = useState({});
  useEffect(() => {
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    let cancelled = false;
    const load = (k, u) => new Promise((res) => loader.load(u, (t) => res([k, t]), undefined, () => res([k, null])));
    Promise.all(Object.entries(urls).map(([k, u]) => load(k, u))).then((arr) => {
      if (cancelled) return;
      const o = {};
      arr.forEach(([k, t]) => { if (t) { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; } o[k] = t; });
      setTex(o);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, []);
  return tex;
}

function Sun() {
  const ref = useRef();
  const coronaRef = useRef();
  useFrame(({ clock }, dt) => {
    if (ref.current) ref.current.rotation.y += dt * 0.05;
    if (coronaRef.current) {
      const s = 1 + Math.sin(clock.getElapsedTime() * 0.8) * 0.04;
      coronaRef.current.scale.set(s, s, s);
    }
  });
  return h(
    "group",
    null,
    h("mesh", { ref },
      h("sphereGeometry", { args: [2.2, 64, 64] }),
      h("meshBasicMaterial", { color: "#FFC74A" })
    ),
    h("mesh", { ref: coronaRef, scale: [1.35, 1.35, 1.35] },
      h("sphereGeometry", { args: [2.2, 32, 32] }),
      h("meshBasicMaterial", {
        color: "#FF8A2A", transparent: true, opacity: 0.32, side: THREE.BackSide,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    ),
    h("mesh", { scale: [2.0, 2.0, 2.0] },
      h("sphereGeometry", { args: [2.2, 32, 32] }),
      h("meshBasicMaterial", {
        color: "#FF6A1A", transparent: true, opacity: 0.10, side: THREE.BackSide,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    ),
    h("pointLight", { color: "#FFD89B", intensity: 3, distance: 80, decay: 1.6 })
  );
}

function OrbitRing({ radius, color = "#3FAEFF", opacity = 0.15 }) {
  const obj = useMemo(() => {
    const points = [];
    for (let i = 0; i <= 256; i++) {
      const a = (i / 256) * Math.PI * 2;
      points.push(new THREE.Vector3(Math.cos(a) * radius, 0, Math.sin(a) * radius));
    }
    const g = new THREE.BufferGeometry().setFromPoints(points);
    const m = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
    return new THREE.LineLoop(g, m);
  }, [radius, color, opacity]);
  return h("primitive", { object: obj });
}

function Planet({ radius, distance, speed, color, name, atmosphere, tilt = 0, hasRings, onSelect, isSelected, mapTexture, cloudsTexture, emissive, emissiveIntensity = 0.15, axialPeriodMul = 1 }) {
  const ref = useRef();
  const meshRef = useRef();
  const cloudsRef = useRef();
  const haloRef = useRef();
  useFrame(({ clock }, dt) => {
    const t = clock.getElapsedTime() * speed;
    if (ref.current) ref.current.position.set(Math.cos(t) * distance, 0, Math.sin(t) * distance);
    if (meshRef.current) meshRef.current.rotation.y += dt * 0.5 * axialPeriodMul;
    if (cloudsRef.current) cloudsRef.current.rotation.y += dt * 0.65 * axialPeriodMul;
    if (haloRef.current && isSelected) {
      const s = 1 + Math.sin(clock.getElapsedTime() * 4) * 0.1;
      haloRef.current.scale.set(s, s, s);
    }
  });
  const baseMat = mapTexture
    ? h("meshPhongMaterial", {
        map: mapTexture, shininess: 8,
        emissive: new THREE.Color(emissive || "#000000"),
        emissiveIntensity,
      })
    : h("meshStandardMaterial", {
        color, emissive: emissive || color, emissiveIntensity,
        roughness: 0.65, metalness: 0.15,
      });
  return h(
    "group",
    { ref },
    // selection halo
    h("mesh", { ref: haloRef, scale: isSelected ? [1.8, 1.8, 1.8] : [0, 0, 0] },
      h("sphereGeometry", { args: [radius, 24, 24] }),
      h("meshBasicMaterial", {
        color: "#00D4FF", transparent: true, opacity: 0.25, side: THREE.BackSide,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    ),
    h(
      "group",
      { rotation: [tilt, 0, 0], onClick: (e) => { e.stopPropagation(); onSelect && onSelect(name); } },
      h("mesh", { ref: meshRef }, h("sphereGeometry", { args: [radius, 48, 48] }), baseMat),
      cloudsTexture
        ? h("mesh", { ref: cloudsRef },
            h("sphereGeometry", { args: [radius * 1.012, 48, 48] }),
            h("meshLambertMaterial", { map: cloudsTexture, transparent: true, opacity: 0.55, depthWrite: false }))
        : null,
      atmosphere
        ? h("mesh", { scale: [1.12, 1.12, 1.12] },
            h("sphereGeometry", { args: [radius, 32, 32] }),
            h("meshBasicMaterial", {
              color: atmosphere, transparent: true, opacity: 0.18,
              side: THREE.BackSide, blending: THREE.AdditiveBlending, depthWrite: false,
            }))
        : null,
      hasRings
        ? h("mesh", { rotation: [Math.PI / 2.3, 0, 0] },
            h("ringGeometry", { args: [radius * 1.4, radius * 2.2, 96] }),
            h("meshBasicMaterial", {
              color: "#E2C97A", transparent: true, opacity: 0.6, side: THREE.DoubleSide,
            }))
        : null
    )
  );
}

function AsteroidBelt({ innerR = 18, outerR = 22, count = 1500 }) {
  const ref = useRef();
  const geo = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = innerR + Math.random() * (outerR - innerR);
      const a = Math.random() * Math.PI * 2;
      positions[i * 3] = Math.cos(a) * r;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 0.4;
      positions[i * 3 + 2] = Math.sin(a) * r;
      const v = 0.55 + Math.random() * 0.35;
      colors[i * 3] = v; colors[i * 3 + 1] = v * 0.85; colors[i * 3 + 2] = v * 0.7;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    return g;
  }, [innerR, outerR, count]);
  useFrame((_, dt) => { if (ref.current) ref.current.rotation.y += dt * 0.02; });
  return h(
    "points",
    { ref, geometry: geo },
    h("pointsMaterial", { size: 0.08, vertexColors: true, sizeAttenuation: true, transparent: true, opacity: 0.85 })
  );
}

function StarField({ count = 4500 }) {
  const geo = useMemo(() => {
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 60 + Math.random() * 80;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return g;
  }, [count]);
  return h(
    "points",
    { geometry: geo },
    h("pointsMaterial", { size: 0.07, color: "#ffffff", sizeAttenuation: true, transparent: true, opacity: 0.85 })
  );
}

// Distances scaled (real proportions compressed)
const PLANETS = [
  { name: "Mercury",  radius: 0.42, distance: 4.6,  speed: 0.85, color: "#C7B79A", tilt: 0.03, axialPeriodMul: 0.4 },
  { name: "Venus",    radius: 0.78, distance: 6.2,  speed: 0.62, color: "#E8C988", atmosphere: "#FFD988", tilt: 0.05, axialPeriodMul: 0.2 },
  { name: "Earth",    radius: 0.82, distance: 8.2,  speed: 0.5,  color: "#3FAEFF", atmosphere: "#3FAEFF", tilt: 0.41, axialPeriodMul: 1.0, textured: true },
  { name: "Mars",     radius: 0.56, distance: 10.6, speed: 0.42, color: "#E1654E", tilt: 0.44, axialPeriodMul: 0.95 },
  { name: "Jupiter",  radius: 1.6,  distance: 16,   speed: 0.22, color: "#D9B583", tilt: 0.05, atmosphere: "#F2D3A2", axialPeriodMul: 2.4 },
  { name: "Saturn",   radius: 1.35, distance: 21,   speed: 0.17, color: "#E2C97A", tilt: 0.47, hasRings: true, axialPeriodMul: 2.2 },
  { name: "Uranus",   radius: 1.0,  distance: 26,   speed: 0.13, color: "#7BE6E8", atmosphere: "#7BE6E8", tilt: 1.7, axialPeriodMul: 1.7 },
  { name: "Neptune",  radius: 0.98, distance: 30,   speed: 0.10, color: "#2B6FFF", atmosphere: "#2B6FFF", tilt: 0.49, axialPeriodMul: 1.5 },
];

function Scene({ onSelect, selected, interactive = true }) {
  const tex = useTextureSet({ day: EARTH_DAY, clouds: EARTH_CLOUDS });
  return h(
    "group",
    null,
    h("color", { attach: "background", args: ["#03040B"] }),
    h("ambientLight", { intensity: 0.18 }),
    h(StarField, { count: 4500 }),
    h(Sun, null),
    ...PLANETS.flatMap((p) => [
      h(OrbitRing, { key: `orbit-${p.name}`, radius: p.distance, color: "#3FAEFF", opacity: 0.12 }),
      h(Planet, {
        key: p.name, ...p,
        mapTexture: p.textured ? tex.day : null,
        cloudsTexture: p.textured ? tex.clouds : null,
        emissive: p.name === "Earth" ? "#001428" : (p.atmosphere || p.color),
        emissiveIntensity: p.name === "Earth" ? 0.18 : 0.12,
        onSelect, isSelected: selected === p.name,
      }),
    ]),
    h(AsteroidBelt, { innerR: 12.4, outerR: 14.4, count: 1400 }),
    h(AsteroidBelt, { innerR: 33, outerR: 38, count: 2000 }), // Kuiper Belt
    interactive
      ? h(OrbitControls, {
          enablePan: true, enableZoom: true, enableRotate: true,
          enableDamping: true, dampingFactor: 0.08,
          rotateSpeed: 0.6, zoomSpeed: 0.9, panSpeed: 0.8,
          minDistance: 6, maxDistance: 80,
          touches: { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN },
        })
      : null
  );
}

export default function SolarSystem3D({ onSelect, selected, className = "", interactive = true }) {
  return (
    <div className={className} style={{ position: "absolute", inset: 0 }}>
      <Canvas camera={{ position: [0, 18, 28], fov: 55, near: 0.1, far: 500 }} dpr={[1, 2]}>
        <Scene onSelect={onSelect} selected={selected} interactive={interactive} />
      </Canvas>
    </div>
  );
}

export { PLANETS as SOLAR_PLANETS };
